/**
 * Dice Configuration Service
 * Handles saving and loading user dice preferences to/from Firestore
 */
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import { useUserStore } from '@/stores/userStore'

class DiceConfigService {
  /**
   * Load dice configuration for a user
   * @param {string|null} userId - User ID, defaults to current authenticated user
   * @returns {Promise<Object|null>} User's dice configuration or null if not found
   */
  static async getUserConfig(userId = null) {
    try {
      const userStore = useUserStore()
      const uid = userId || userStore.user?.uid

      if (!uid) {
        throw new Error('User not authenticated')
      }

      const configDoc = await getDoc(doc(db, 'users', uid, 'preferences', 'dice'))
      return configDoc.exists() ? configDoc.data() : null
    } catch (error) {
      console.error('Error loading dice config:', error)
      throw error
    }
  }

  /**
   * Save dice configuration for a user
   * @param {Object} config - Dice configuration object
   * @param {string|null} userId - User ID, defaults to current authenticated user
   * @returns {Promise<Object>} Saved configuration with metadata
   */
  static async saveUserConfig(config, userId = null) {
    try {
      const userStore = useUserStore()
      const uid = userId || userStore.user?.uid

      if (!uid) {
        throw new Error('User not authenticated')
      }

      const configData = {
        ...config,
        updatedAt: new Date().toISOString()
      }

      const docRef = doc(db, 'users', uid, 'preferences', 'dice')
      await setDoc(docRef, configData, { merge: true })

      return configData
    } catch (error) {
      console.error('Error saving dice config:', error)
      throw error
    }
  }

  /**
   * Get default dice configuration
   * @returns {Object} Default configuration object
   */
  static getDefaultConfig() {
    return {
      playSound: true,
      use3DMode: true,
      diceSkin: 'default',
      diceColor: '#ffffff',
      letterColor: '#000000',
      animationSpeed: 'normal',
      autoRollDelay: 1000,
      showRollHistory: true,
      maxHistoryEntries: 100
    }
  }

  /**
   * Reset user configuration to defaults
   * @param {string|null} userId - User ID, defaults to current authenticated user
   * @returns {Promise<Object>} Reset configuration
   */
  static async resetToDefaults(userId = null) {
    const defaultConfig = this.getDefaultConfig()
    return await this.saveUserConfig(defaultConfig, userId)
  }
}

export default DiceConfigService
